import React, { useEffect, useMemo, useState } from 'react'
import { Alert, Autocomplete, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, FormGroup, IconButton, MenuItem, TextField, Typography } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import CloseIcon from '@mui/icons-material/Close'
import { useSnackbar } from 'notistack'
import palette from '../../../../theme/palette'
import { useInternalUsersQuery, useNabdSaveMemberMutation } from '../../../redux/ptlNabdApiSlice'
import { TEAM_LABEL, TEAM_OPTIONS, rolesLabel } from '../constants'
import { FormField, inputSx } from '../eddBreachUi'
import ConfirmDialog from './ConfirmDialog'

const EMPTY = { email: '', name: '', roles: ['PTL_OPS'], subTeam: 'CORE_OPS', designation: '', active: true, senior: false }

/**
 * Add / edit a PTL team member (artifact "Add a team member"). The person is picked from the admin-panel users (searched
 * on the server as you type, GET auth/internal-users?q=) so the login e-mail is stored (that is what "Acting as" matches); the name pre-fills from the user and stays editable.
 * Rules mirror NabdTeamService: name ≥ 3 and unique, at least one role, designation ≥ 3, sub-team for PTL Ops, one member per login.
 */
export default function MemberDialog({ open, member, team, openCount = 0, onClose }) {
  const { enqueueSnackbar } = useSnackbar()
  const [userInput, setUserInput] = useState('')
  const [userQuery, setUserQuery] = useState('')
  const { data: users = [], isFetching: usersLoading } = useInternalUsersQuery(userQuery, { skip: !open })
  const [save, { isLoading }] = useNabdSaveMemberMutation()
  const [v, setV] = useState(EMPTY)
  const [errors, setErrors] = useState({})
  const [serverError, setServerError] = useState('')
  const [confirmOpen, setConfirmOpen] = useState(false)
  const editing = !!member

  // debounce the search so typing does not fire a request per key
  useEffect(() => {
    const t = setTimeout(() => setUserQuery(userInput.trim()), 300)
    return () => clearTimeout(t)
  }, [userInput])

  useEffect(() => {
    if (!open) return
    setUserInput('')
    setUserQuery('')
    setErrors({})
    setServerError('')
    setV(member ? {
      email: member.email || '', name: member.name || '', roles: member.roles?.length ? member.roles : ['PTL_OPS'], subTeam: member.subTeam || 'CORE_OPS',
      designation: member.designation || '', active: !!member.active, senior: !!member.senior,
    } : EMPTY)
  }, [open, member])

  const set = (patch) => setV((cur) => ({ ...cur, ...patch }))
  const isOps = v.roles.includes('PTL_OPS')
  // keep the roles in TEAM_OPTIONS order whatever the click order
  const toggleRole = (team) => set({
    roles: TEAM_OPTIONS.map((o) => o.value).filter((r) => (r === team ? !v.roles.includes(team) : v.roles.includes(r))),
  })
  // the search results may not contain the user already picked (or the member's login when editing), so keep it as an option
  const pickedUser = useMemo(() => {
    if (!v.email) return null
    return users.find((u) => (u.email || '').toLowerCase() === v.email.toLowerCase()) || { email: v.email, fullName: v.name }
  }, [users, v.email, v.name])
  const userOptions = useMemo(() => {
    const list = users.filter((u) => u.email)
    return pickedUser && !list.some((u) => u.email.toLowerCase() === pickedUser.email.toLowerCase()) ? [pickedUser, ...list] : list
  }, [users, pickedUser])

  const validate = () => {
    const e = {}
    const name = v.name.trim()
    if (!v.email) e.email = 'Pick the admin-panel user this member logs in as.'
    else {
      const sameLogin = team.find((t) => t.id !== member?.id && (t.email || '').toLowerCase() === v.email.toLowerCase())
      if (sameLogin) e.email = `This login is already on the team as ${sameLogin.name}.`
    }
    if (name.length < 3) e.name = 'Enter the full name.'
    else {
      const clash = team.find((t) => t.id !== member?.id && (t.name || '').toLowerCase() === name.toLowerCase())
      if (clash) e.name = `${name} is already on the PTL team (${rolesLabel(clash)}).`
    }
    if (!v.roles.length) e.roles = 'Pick at least one role.'
    if (v.designation.trim().length < 3) e.designation = 'Enter the designation.'
    return e
  }

  const submit = async (confirmed = false) => {
    const e = validate()
    setErrors(e)
    if (Object.keys(e).length) return
    if (!confirmed && editing && member.active && !v.active && openCount > 0) { setConfirmOpen(true); return }
    setConfirmOpen(false)
    const body = { ...v, name: v.name.trim(), designation: v.designation.trim(), subTeam: isOps ? v.subTeam : null }
    try {
      await save({ id: member?.id, body }).unwrap()
      enqueueSnackbar(editing ? `${body.name} updated` : `${body.name} added as ${rolesLabel(v)}`, { variant: 'success' })
      onClose(true)
    } catch (err) {
      const msg = err?.data?.message || err?.message || 'Something went wrong'
      setServerError(msg)
      enqueueSnackbar(msg, { variant: 'error' })
    }
  }

  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth={false} PaperProps={{ sx: { width: '100%', maxWidth: 640, borderRadius: '8px', m: 2 } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, p: '16px 20px', borderBottom: `1px solid ${palette.grey[300]}` }}>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 15, fontWeight: 600 }}>{editing ? `Edit ${member.name}` : 'Add a team member'}</Typography>
          <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mt: 0.5 }}>Roles decide which delays this person can be assigned. Tick both if they do both jobs.</Typography>
        </Box>
        <IconButton size='small' onClick={() => onClose(false)} aria-label='Close'><CloseIcon fontSize='small' /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: '20px !important', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {serverError && <Alert severity='error' sx={{ py: 0, fontSize: 12 }}>{serverError}</Alert>}
        <FormField label='Admin-panel user' required error={errors.email} hint='The login this member uses. "Acting as" is matched on it.'>
          <Autocomplete size='small' options={userOptions} loading={usersLoading} value={pickedUser}
            getOptionLabel={(u) => `${u.fullName || u.username || ''} (${u.email})`} isOptionEqualToValue={(a, b) => a.email === b.email}
            filterOptions={(x) => x} onInputChange={(_e, text, reason) => setUserInput(reason === 'input' ? text : '')}
            onChange={(_e, u) => set({ email: u?.email || '', name: v.name && editing ? v.name : (u?.fullName || u?.username || '') })}
            ListboxProps={{ sx: { fontSize: 13 } }} noOptionsText={usersLoading ? 'Loading users…' : 'No matching user'}
            renderInput={(p) => <TextField {...p} sx={inputSx} placeholder='Search by name or email' error={!!errors.email}
              inputProps={{ ...p.inputProps, autoComplete: 'off' }} />} />
        </FormField>
        <FormField label='Full name' required error={errors.name}>
          <TextField fullWidth size='small' sx={inputSx} value={v.name} onChange={(e) => set({ name: e.target.value })}
            inputProps={{ maxLength: 60 }} placeholder='Ishant Sharma' error={!!errors.name} />
        </FormField>
        <FormField label='Roles' required error={errors.roles}>
          <FormGroup>
            {TEAM_OPTIONS.map((o) => (
              <FormControlLabel key={o.value} sx={{ '& .MuiFormControlLabel-label': { fontSize: 13 } }}
                control={<Checkbox size='small' checked={v.roles.includes(o.value)} onChange={() => toggleRole(o.value)} />}
                label={`${TEAM_LABEL[o.value]}, ${o.hint}`} />
            ))}
          </FormGroup>
        </FormField>
        {isOps && (
          <FormField label='Operations sub-team' required hint='Core Operations runs the live shipment. FTR works on the upstream causes so the breach does not recur.'>
            <TextField select fullWidth size='small' sx={inputSx} value={v.subTeam} onChange={(e) => set({ subTeam: e.target.value })}>
              <MenuItem value='CORE_OPS' sx={{ fontSize: 13 }}>Core Operations</MenuItem>
              <MenuItem value='FTR' sx={{ fontSize: 13 }}>First Time Right, FTR</MenuItem>
            </TextField>
          </FormField>
        )}
        <FormField label='Designation' required error={errors.designation}>
          <TextField fullWidth size='small' sx={inputSx} value={v.designation} onChange={(e) => set({ designation: e.target.value })}
            inputProps={{ maxLength: 60 }} placeholder={isOps ? 'Senior Executive, PTL Operations' : 'Key Account Manager'} error={!!errors.designation} />
        </FormField>
        <FormField label='Status'>
          <TextField select fullWidth size='small' sx={inputSx} value={v.active ? '1' : '0'} onChange={(e) => set({ active: e.target.value === '1' })}>
            <MenuItem value='1' sx={{ fontSize: 13 }}>Active, appears in assignment dropdowns</MenuItem>
            <MenuItem value='0' sx={{ fontSize: 13 }}>Inactive, hidden from assignment</MenuItem>
          </TextField>
        </FormField>
        <FormField label='Can reopen a closed record' required
          hint='A closed delivered shipment is locked. Only a member marked senior here can reopen it, and only with a written reason that goes into the trail.'>
          <TextField select fullWidth size='small' sx={inputSx} value={v.senior ? '1' : '0'} onChange={(e) => set({ senior: e.target.value === '1' })}>
            <MenuItem value='0' sx={{ fontSize: 13 }}>No, this member cannot reopen a closed record</MenuItem>
            <MenuItem value='1' sx={{ fontSize: 13 }}>Yes, senior member</MenuItem>
          </TextField>
        </FormField>
      </DialogContent>
      <DialogActions sx={{ px: 2.5, py: 1.75, borderTop: `1px solid ${palette.grey[300]}` }}>
        <Button onClick={() => onClose(false)} disabled={isLoading}>Cancel</Button>
        <LoadingButton variant='contained' loading={isLoading} onClick={() => submit(false)}>Save member</LoadingButton>
      </DialogActions>
      <ConfirmDialog open={confirmOpen} title={`Deactivate ${member?.name}?`} danger confirmLabel='Deactivate'
        message={`${member?.name} holds ${openCount} open shipment(s). Deactivating hides them from new assignment and removes their merchant mappings; the open shipments stay with them.`}
        onClose={() => setConfirmOpen(false)} onConfirm={() => submit(true)} loading={isLoading} />
    </Dialog>
  )
}
